/**
 * Stats fake en dur pour l'hôtel démo hId=73 (« Hôtel Démo V2 »).
 * Priorité front : court-circuite les appels API quand isDemoHotel73(hId).
 *
 * Cohérence métier :
 * - Dashboard € = LuniShower (sheet eau) + ménage (actions)
 * - Eau : card 1 256 000 L ; graphiques mensuels en m³ (juin = 184) ;
 *   (conso sans − conso avec) = économie d'eau du mois
 */

export const DEMO_HOTEL_73_ID = 73;

export const isDemoHotel73 = (hId) => Number(hId) === DEMO_HOTEL_73_ID;

export const getDemoHotel73MyHotelsCard = () => ({
  hId: DEMO_HOTEL_73_ID,
  hName: "Hôtel Démo",
  hCity: "Paris",
  installationDate: INSTALLATION_DATE,
  hInstallationDate: INSTALLATION_DATE,
  savings: {
    financialEur: DASHBOARD_MONEY_TOTAL,
    waterLiters: TOTAL_WATER_LITERS,
    energyKwh: TOTAL_ENERGY_KWH,
  },
});

const INSTALLATION_DATE = "2026-01-01";

/** Totaux card : dashboard = ménage + LuniShower */
const DASHBOARD_MONEY_TOTAL = 8754;
const MENAGE_MONEY_TOTAL = 6223;
const LUNISHOWER_MONEY_TOTAL = DASHBOARD_MONEY_TOTAL - MENAGE_MONEY_TOTAL; // 2531

/**
 * Courbe dashboard (totaux mensuels image).
 * Split : luniShowerEuros (sheet) + menageEuros (actions) = euros.
 */
const FINANCIAL_MONTHS_2026 = [
  { month: "2026-01", euros: 1330, luniShowerEuros: 420, menageEuros: 910 },
  { month: "2026-02", euros: 1145, luniShowerEuros: 380, menageEuros: 765 },
  { month: "2026-03", euros: 1320, luniShowerEuros: 430, menageEuros: 890 },
  { month: "2026-04", euros: 1410, luniShowerEuros: 450, menageEuros: 960 },
  { month: "2026-05", euros: 1360, luniShowerEuros: 431, menageEuros: 929 },
  { month: "2026-06", euros: 1289, luniShowerEuros: 420, menageEuros: 869 },
];

/**
 * Conso douches + ÉCO D'EAU — cohérents avec la card :
 * 1 256 000 L (= 1 256 m³) au total, +184 000 L (= 184 m³) en juin.
 * Pour chaque mois : (conso sans − conso avec) = économie d'eau (m³).
 * Réduction ~35 % (avec ≈ 65 % du témoin).
 */
const WATER_ENERGY_MONTHS_2026 = [
  // eco m³ | sans | avec (= sans − eco) | kWh
  { month: "2026-01", waterSavingsM3: 223, consoTemoin: 637, consoLunishower: 414, energySavingsKwh: 520 },
  { month: "2026-02", waterSavingsM3: 214, consoTemoin: 611, consoLunishower: 397, energySavingsKwh: 480 },
  { month: "2026-03", waterSavingsM3: 206, consoTemoin: 589, consoLunishower: 383, energySavingsKwh: 510 },
  { month: "2026-04", waterSavingsM3: 223, consoTemoin: 637, consoLunishower: 414, energySavingsKwh: 540 },
  { month: "2026-05", waterSavingsM3: 206, consoTemoin: 589, consoLunishower: 383, energySavingsKwh: 525 },
  { month: "2026-06", waterSavingsM3: 184, consoTemoin: 526, consoLunishower: 342, energySavingsKwh: 412 },
];

/** Card « Économies d'eau » */
const TOTAL_WATER_LITERS = 1256000; // Σ waterSavingsM3 × 1000
const LAST_MONTH_WATER_LITERS = 184000; // juin

const TOTAL_ENERGY_KWH = WATER_ENERGY_MONTHS_2026.reduce((sum, row) => sum + row.energySavingsKwh, 0);
const LAST_MONTH_ENERGY_KWH = WATER_ENERGY_MONTHS_2026[WATER_ENERGY_MONTHS_2026.length - 1].energySavingsKwh;

const LAST_MONTH_FINANCIAL = FINANCIAL_MONTHS_2026[FINANCIAL_MONTHS_2026.length - 1];

const withPublishedFlags = (month) => ({
  month,
  hasMonthlyRecord: true,
  showOnPlatform: 1,
  isPlatformMonthPending: false,
});

export const getDemoHotel73DashboardStats = () => {
  const chartData = FINANCIAL_MONTHS_2026.map(({ month, luniShowerEuros, menageEuros, euros }) => ({
    ...withPublishedFlags(month),
    // Dashboard chart = water + energy + actions
    totalWaterSavedInEuros: luniShowerEuros,
    totalEnergySavedInEuros: 0,
    totalActionsSavedInEuros: menageEuros,
    // Page eau (luniShowerOnly) = sheet uniquement
    totalLuniShowerSavedInEuros: luniShowerEuros,
    totalActionsWaterSavedLiters: 0,
    totalActionsCO2SavedKg: 0,
    graphTotalWaterSaved: 0,
    graphTotalEnergySaved: 0,
    greenMilesStays: 0,
    // garde le total mensuel attendu (debug / cohérence)
    _demoTotalEuros: euros,
  }));

  return {
    success: "OK",
    installationDate: INSTALLATION_DATE,
    platformInstallationMissing: false,
    stats: {
      numberOfShowers: 0,
      totalWaterSaved: TOTAL_WATER_LITERS,
      totalMoneySaved: DASHBOARD_MONEY_TOTAL,
      totalGreenMilesStays: 412,
      financialSavingsVariation: LAST_MONTH_FINANCIAL.euros,
      greenMilesStaysVariation: 36,
      waterSavedVariation: LAST_MONTH_WATER_LITERS,
      averageReductionPercentage: 35,
    },
    chartData,
  };
};

export const getDemoHotel73WaterSummary = () => ({
  value: TOTAL_WATER_LITERS,
  variation: LAST_MONTH_WATER_LITERS,
});

export const getDemoHotel73WaterStats = () => ({
  success: "OK",
  installationDate: INSTALLATION_DATE,
  platformInstallationMissing: false,
  waterSaved: {
    value: TOTAL_WATER_LITERS,
    variation: LAST_MONTH_WATER_LITERS,
    unit: "L",
  },
  moneySaved: {
    value: LUNISHOWER_MONEY_TOTAL,
    variation: LAST_MONTH_FINANCIAL.luniShowerEuros,
    currency: "EUR",
  },
  waterSavedByProduct: {
    total: TOTAL_WATER_LITERS,
    details: [],
  },
  showerConsumption: WATER_ENERGY_MONTHS_2026.map((row) => ({
    ...withPublishedFlags(row.month),
    consoTemoin: row.consoTemoin,
    consoLunishower: row.consoLunishower,
  })),
  monthlySavings: WATER_ENERGY_MONTHS_2026.map((row) => ({
    ...withPublishedFlags(row.month),
    waterSavingsM3: row.waterSavingsM3,
    energySavingsKwh: row.energySavingsKwh,
  })),
});

export const getDemoHotel73PollutionStats = () => ({
  success: "OK",
  energySaved: {
    value: TOTAL_ENERGY_KWH,
    variation: LAST_MONTH_ENERGY_KWH,
    unit: "kWh",
  },
  co2Saved: {
    value: 0,
    variation: 0,
    unit: "kg",
  },
});

export const getDemoHotel73CleaningStats = () => ({
  installationDate: INSTALLATION_DATE,
  servicesDeclined: {
    value: 695,
    variation: 52,
  },
  amountSaved: {
    value: 4865,
    variation: LAST_MONTH_FINANCIAL.menageEuros,
  },
  waterSaved: {
    total: 0,
    details: [],
  },
  servicesByType: [
    {
      id: 7301,
      rewardName: "Refus du ménage",
      servicesDeclined: 286,
      actSavingsEuro: 7,
      waterSaved: 0,
      eurosSaved: 2002,
    },
    {
      id: 7302,
      rewardName: "Refus changement serviette",
      servicesDeclined: 194,
      actSavingsEuro: 7,
      waterSaved: 0,
      eurosSaved: 1358,
    },
    {
      id: 7303,
      rewardName: "Refus changement draps",
      servicesDeclined: 215,
      actSavingsEuro: 7,
      waterSaved: 0,
      eurosSaved: 1505,
    },
  ],
  servicesDeclinedSummary: [
    {
      id: 1,
      email: "housekeeping@hotel-demo-v2.com",
      language: "FR",
      lastSentAt: "2026-06-27",
    },
  ],
});

export const getDemoHotel73GreenMilesStats = () => ({
  success: "OK",
  installationDate: INSTALLATION_DATE,
  summary: {
    greenMilesStays: {
      value: 412,
      variation: 36,
    },
    greenMilesUsage: {
      value: 35,
      variation: 0,
    },
    greenMilesScanRate: {
      value: 8,
      variation: 0,
    },
  },
  actions: [
    { id: 7301, type: "LUNICLEAN", name: "Refus du ménage", nbEffectue: 286, greenMilesReward: 200 },
    { id: 7302, type: "YESNOMAIL", name: "Refus changement serviette", nbEffectue: 194, greenMilesReward: 150 },
    { id: 7303, type: "LUNISHOWER", name: "LuniShower", nbEffectue: 1256, greenMilesReward: 200 },
    { id: 7304, type: "OTHER", name: "Refus chausson", nbEffectue: 173, greenMilesReward: 100 },
    { id: 7305, type: "OTHER", name: "0 Plastiques", nbEffectue: 149, greenMilesReward: 150 },
    { id: 7306, type: "OTHER", name: "Labellisation Clef Verte", nbEffectue: 87, greenMilesReward: 100 },
  ],
  rewards: [
    // Templates GreenMiles standards (mêmes noms / types / coûts que les autres hôtels)
    { rwId: 7310, name: "Don d'eau de 50 L", rwType: "EAU", unlockedCount: 65, greenMilesCost: 50 },
    { rwId: 7311, name: "Don d'eau de 100 L", rwType: "EAU", unlockedCount: 38, greenMilesCost: 100 },
    { rwId: 7312, name: "Thé ou café offert", rwType: "SLIDER", unlockedCount: 52, greenMilesCost: 80 },
    { rwId: 7313, name: "10% au SPA", rwType: "SLIDER", unlockedCount: 24, greenMilesCost: 100 },
    { rwId: 7314, name: "30% sur un cocktail", rwType: "SLIDER", unlockedCount: 31, greenMilesCost: 10 },
  ],
});
